// The partner workspace taxonomy. One registry drives both the mobile tab bar
// and the desktop sidebar, so the two shells cannot drift into different
// products.
//
// WHY THERE ARE NO MENUS
//
// It used to be Home, Clients, Work, Inbox, More — five slots, of which three
// (Home, Work, More) were MENUS: screens whose whole content was a list of
// links to somewhere else. A menu has to restate what it links to, so the same
// information appeared twice under different names; the Home stack held one
// screen, so every tap on it was a cross-tab jump; and pushing real work down a
// level meant screens got registered in two stacks at once, which is why Back
// behaved differently depending on which tab you entered from. Every primary
// slot is somewhere you can actually be.
//
// FAMILIES, NOT CLIENTS AND WORK
//
// The next version still split one household across three lists: its intake
// sat in Work, its funding "simulation" behind a Funding tool, and after
// conversion the same family reappeared in Clients. Partners — All Needs
// Planning first — think in families moving through stages, so the spine is
// Families: one row per household (utilities/partnerFamilies.ts), stage
// INVITED / INTAKE → PLANNING → CLIENT, and one workspace per family holding
// its Fact Finder, funding plan, forms and plans. Funding plans is a primary
// destination for the partners who have the module, because for them it is
// the work; every row in it opens that family's workspace, so there is still
// exactly one planner in the product.

export type PartnerWorkspaceTabName = 'Families' | 'Funding' | 'Inbox' | 'More';

export interface PartnerWorkspaceTab {
  name: PartnerWorkspaceTabName;
  label: string;
  /** The phone tab bar's label, where width is scarce. Defaults to `label`. */
  shortLabel?: string;
  icon: string;
  sidebarIcon: [string, string];
  /** An account feature that must be on for the destination to be offered. */
  feature?: 'all_needs_planning_enabled';
}

/** The primary destinations. Every one of them is a screen with data on it. */
export const PARTNER_WORKSPACE_TABS: readonly PartnerWorkspaceTab[] = [
  { name: 'Families', label: 'Families', icon: 'people-group', sidebarIcon: ['fad', 'people-group'] },
  // Behind the planner's own gate (account_features.all_needs_planning_enabled,
  // which /anp/me re-checks on the screen), so it never leads a partner without
  // the module to a screen that turns them away.
  { name: 'Funding', label: 'Funding plans', shortLabel: 'Funding', icon: 'chart-line', sidebarIcon: ['fad', 'chart-line'], feature: 'all_needs_planning_enabled' },
  { name: 'Inbox', label: 'Inbox', icon: 'comments', sidebarIcon: ['fad', 'comments'] },
] as const;

/** Whether a primary destination is offered to this account. */
export function isPartnerTabVisible(tab: PartnerWorkspaceTab, features: Record<string, unknown> | null | undefined): boolean {
  return !tab.feature || features?.[tab.feature] === true;
}

/** The screen each primary destination opens on. */
export const PARTNER_TAB_ROOTS: Record<Exclude<PartnerWorkspaceTabName, 'More'>, string> = {
  Families: 'PartnerFamilies',
  Funding: 'ANPSimulations',
  Inbox: 'PartnerMessages',
};

/**
 * Account-level tools. These are real destinations too — they are separated
 * from the destinations above because they are things you visit occasionally,
 * not the work itself.
 *
 * The two shells present them differently ON PURPOSE, because their constraints
 * differ. A phone tab bar runs out of room, so genuine overflow behind a "More"
 * tab is the right pattern there. A desktop sidebar has a column of vertical
 * space doing nothing, so it lists them inline under a rule and the More tab
 * does not exist. What they must NOT differ on is which tools are offered: both
 * render getVisiblePartnerTools below.
 */
export interface PartnerWorkspaceTool {
  key: string;
  label: string;
  description: string;
  icon: string;
  sidebarIcon: [string, string];
  /** The capability key in PARTNER_CAPABILITY_ROUTES this opens. */
  capability: string;
  /** An account feature that must be on for the tool to be offered at all.
   *  The same flag the destination itself is gated on, so the tool never
   *  leads to a screen that turns the partner away. */
  feature?: 'all_needs_planning_enabled';
}

export const PARTNER_WORKSPACE_TOOLS: readonly PartnerWorkspaceTool[] = [
  { key: 'documents', label: 'Documents', description: 'Your files and client plan documents.', icon: 'folder-open', sidebarIcon: ['fad', 'folder-open'], capability: 'clientDocuments' },
  { key: 'ellie', label: 'Ask Ellie', description: 'Ask questions across the partner account.', icon: 'sparkles', sidebarIcon: ['fad', 'sparkles'], capability: 'accountAssistant' },
  { key: 'insights', label: 'Insights', description: 'Population-level signals across clients.', icon: 'waveform-lines', sidebarIcon: ['fad', 'waveform-lines'], capability: 'insights' },
  { key: 'team', label: 'Team', description: 'Manage people in your partner account.', icon: 'users-gear', sidebarIcon: ['fad', 'users-gear'], capability: 'team' },
  { key: 'settings', label: 'Settings', description: 'Branding, verification, profile, and account controls.', icon: 'gear', sidebarIcon: ['fad', 'gear'], capability: 'partnerSettings' },
  { key: 'support', label: 'Support', description: 'Message the Eleplan support team.', icon: 'life-ring', sidebarIcon: ['fad', 'life-ring'], capability: 'support' },
] as const;

/** Whether a tool is offered to this account: no feature named, or that feature on. */
export function isPartnerToolVisible(tool: PartnerWorkspaceTool, features: Record<string, unknown> | null | undefined): boolean {
  return !tool.feature || features?.[tool.feature] === true;
}

export interface PartnerToolVisibilityContext {
  features: Record<string, unknown> | null | undefined;
  /** account.partner — Insights is population health over SPONSORED accounts. */
  isPartnerAccount: boolean;
  /** The account-chat flag and the caller's role allow Ask Ellie. */
  canUseAccountChat: boolean;
}

/**
 * The tools this account is offered, on BOTH shells. The phone More screen
 * used to hide Ask Ellie and Insights behind their real gates while the
 * desktop sidebar listed them for everyone, so a caseworker without account
 * chat clicked into a screen that refused them.
 */
export function getVisiblePartnerTools(ctx: PartnerToolVisibilityContext): PartnerWorkspaceTool[] {
  return PARTNER_WORKSPACE_TOOLS.filter((tool) => {
    if (!isPartnerToolVisible(tool, ctx.features)) return false;
    if (tool.key === 'ellie') return ctx.canUseAccountChat;
    if (tool.key === 'insights') return ctx.isPartnerAccount;
    return true;
  });
}

/** The phone-only overflow tab. Absent from the desktop sidebar by design. */
export const PARTNER_OVERFLOW_TAB: PartnerWorkspaceTab = {
  name: 'More', label: 'More', icon: 'ellipsis', sidebarIcon: ['fas', 'ellipsis'],
};

export interface PartnerCapabilityRoute {
  tab: PartnerWorkspaceTabName;
  screen: string;
}

/**
 * Where every shipped capability lives. This is the regression contract: a
 * screen may move, but removing one requires editing this map deliberately.
 *
 * It is only worth anything if it is TRUE — partnerWorkspace.test.ts checks
 * that each entry is registered in the stack its tab names AND that a person
 * can press their way to it, not merely that the route name appears somewhere.
 */
export const PARTNER_CAPABILITY_ROUTES: Record<string, PartnerCapabilityRoute> = {
  // Families — the spine: list → family workspace → plan.
  families: { tab: 'Families', screen: 'PartnerFamilies' },
  familyWorkspace: { tab: 'Families', screen: 'PartnerFamily' },
  // The full-screen Fact Finder (the workspace embeds the same screen).
  factFinder: { tab: 'Families', screen: 'PartnerIntakeSurvey' },
  // Release, plan & subscription — embedded in the workspace's Plans tab and
  // still openable on its own (Insights links here).
  clientRecord: { tab: 'Families', screen: 'PartnerCustomerDetail' },
  clientPlans: { tab: 'Families', screen: 'PartnerProjects' },
  planDetail: { tab: 'Families', screen: 'PartnerPlanDetail' },
  invitations: { tab: 'Families', screen: 'PartnerInvites' },
  addClient: { tab: 'Families', screen: 'PartnerProjectCreation' },
  // The standalone planner, opened from a plan ("Open funding planner") or a
  // client record ("Funding"). The workspace embeds the same screen.
  fundingPlanner: { tab: 'Families', screen: 'ANPPlanner' },

  // Funding plans — every planning child across families.
  fundingPlans: { tab: 'Funding', screen: 'ANPSimulations' },

  // Inbox — every conversation, support included. Support used to be
  // registered in two stacks and opened as two independent instances with
  // separate back stacks depending on whether you came from Settings or from
  // Messages. It is a conversation; it belongs where the conversations are.
  messages: { tab: 'Inbox', screen: 'PartnerMessages' },
  support: { tab: 'Inbox', screen: 'PartnerSupportChat' },

  // Tools.
  clientDocuments: { tab: 'More', screen: 'PartnerDocsRoot' },
  insights: { tab: 'More', screen: 'PartnerInsights' },
  accountAssistant: { tab: 'More', screen: 'PartnerEllie' },
  team: { tab: 'More', screen: 'PartnerMembers' },
  partnerSettings: { tab: 'More', screen: 'PartnerSettings' },
};

const OUTSTANDING_INVITATION_STATUSES = new Set(['sent', 'resent', 'registered']);

export function getPartnerWorkSummary(invites: readonly any[] | null | undefined) {
  let pendingIntakes = 0;
  let outstandingInvitations = 0;

  for (const item of invites || []) {
    if (item?.status === 'pending') pendingIntakes += 1;
    else if (OUTSTANDING_INVITATION_STATUSES.has(item?.status)) outstandingInvitations += 1;
  }

  return {
    pendingIntakes,
    outstandingInvitations,
    totalAttention: pendingIntakes + outstandingInvitations,
  };
}

function customerActivityTime(customer: any): number {
  const raw = customer?.last_activity_at || customer?.updated_at || customer?.created_at;
  const parsed = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sortRecentPartnerCustomers(customers: readonly any[] | null | undefined, limit = 3): any[] {
  return [...(customers || [])]
    .sort((a, b) => customerActivityTime(b) - customerActivityTime(a))
    .slice(0, Math.max(0, limit));
}

/**
 * Old partner addresses → their place in the families shell.
 *
 * /clients, /work, /funding and the planner/intake links went out in emails,
 * bookmarks and shared links. Each now lands on its equivalent instead of the
 * default route. Returns null when the path is not a legacy partner path.
 * Query strings are carried over (childKey, projectId, …).
 */
export function rewriteLegacyPartnerPath(path: string | null | undefined): string | null {
  if (!path) return null;
  const [rawPathname, rawQuery = ''] = String(path).split('?');
  const pathname = '/' + rawPathname.replace(/^\/+/, '').replace(/\/+$/, '');
  const query = new URLSearchParams(rawQuery);
  const withQuery = (base: string, extra: Record<string, string> = {}) => {
    const q = new URLSearchParams(query);
    Object.entries(extra).forEach(([k, v]) => q.set(k, v));
    const s = q.toString();
    return s ? `${base}?${s}` : base;
  };

  if (pathname === '/clients' || pathname === '/work') return withQuery('/families');
  if (pathname === '/work/invitations') return withQuery('/families/invitations');
  if (pathname === '/work/convert') return withQuery('/families/add-client');
  if (pathname === '/funding') return withQuery('/funding-plans');

  let m = pathname.match(/^\/work\/intakes\/([^/]+)(?:\/(survey|review))?$/);
  if (m) return withQuery(`/family/i-${encodeURIComponent(decodeURIComponent(m[1]))}`, { tab: 'factfinder' });

  m = pathname.match(/^\/funding\/planner(?:\/([^/]+))?$/);
  if (m) {
    const projectId = query.get('projectId');
    query.delete('projectId');
    if (m[1]) return withQuery(`/family/i-${encodeURIComponent(decodeURIComponent(m[1]))}`, { tab: 'funding' });
    if (projectId) return withQuery(`/family/p-${encodeURIComponent(projectId)}`, { tab: 'funding' });
    return withQuery('/funding-plans');
  }
  return null;
}
